// Season tabs shared by the leader's "Choreografie" and "Grand Finále" pages (requirement 2.1).
//
// * tabs: every season that has items, plus the current season (always shown, even empty,
//   so the coming season is visible before the group's first registration in it);
// * default: the current season when it has items, otherwise the newest season with items
//   (so nobody lands on an empty page between seasons), otherwise the current season;
// * ?rok=YYYY selects another tab; the default tab keeps a clean URL.
// Seasons come from the server (meta.currentSeason), so 2028, 2029 … work without changes.
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMeta } from '../../contexts/MetaContext';
import { seasonTabs } from '../../utils/seasons';

export { seasonTabs };

/** Tabs + selection for a list of items; `yearOf(item)` gives the season of one item. */
export function useSeasonTabs(items, yearOf) {
  const meta = useMeta();
  const [searchParams, setSearchParams] = useSearchParams();
  const current = meta.loaded ? meta.currentSeason : null;
  const { tabs, defaultYear } = useMemo(
    () => seasonTabs((items || []).map(yearOf), current),
    // yearOf is a plain module-level function in both pages
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, current]
  );
  const requested = Number(searchParams.get('rok')) || null;
  const selected = requested && tabs.some((t) => t.year === requested) ? requested : defaultYear;
  const select = (year) => {
    const next = new URLSearchParams(searchParams);
    if (year === defaultYear) next.delete('rok');
    else next.set('rok', String(year));
    setSearchParams(next, { replace: true });
  };
  return { tabs, selected, select, currentSeason: current };
}

export default function SeasonTabs({ tabs, selected, onSelect }) {
  if (tabs.length < 2) return null;
  return (
    <div role="tablist" aria-label="Sezóna" className="flex flex-wrap gap-2">
      {tabs.map(({ year, count }) => (
        <button
          key={year}
          type="button"
          role="tab"
          aria-selected={year === selected}
          onClick={() => onSelect(year)}
          className={`rounded-full border px-4 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
            year === selected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Sezóna {year} <span className="opacity-80">({count})</span>
        </button>
      ))}
    </div>
  );
}
